import type { ClipDims } from '@/core/alignment';

import type { StationBin, StationOffsets, StationParams } from './types';

export interface RangeTotals {
  cut: number;
  fill: number;
  net: number;
  binCount: number;
  lowCoverageCount: number;
  lengthM: number;
  fromInternalM: number;
  toInternalM: number;
  /**
   * Mean coverage over the range, weighted by each bin's reference cell count
   * (`refCellCount`, falling back to `binCellCount` on a run older than the
   * field) — a 300-cell bin at 100 % and a 100-cell bin at 0 % is 75 %
   * covered, not 50 %. `null` when the range holds no reference cells at all,
   * which is "unknown", not "zero".
   */
  coveragePct: number | null;
  /**
   * The reference's share of the swath over the range, weighted by bin
   * length (each bin's figure is its reference area over its own swath area,
   * so the length-weighted mean is the range's reference area over the
   * range's swath area — exact). `null` on a run older than the field.
   */
  swathUsedPct: number | null;
}

/** The coverage denominator a bin carries: reference cells when the run knew
 *  them, corridor cells before that field existed. */
export const coverageWeight = (b: Pick<StationBin, 'refCellCount' | 'binCellCount'>): number =>
  b.refCellCount ?? b.binCellCount;

const EMPTY: RangeTotals = {
  cut: 0,
  fill: 0,
  net: 0,
  binCount: 0,
  lowCoverageCount: 0,
  lengthM: 0,
  fromInternalM: 0,
  toInternalM: 0,
  coveragePct: null,
  swathUsedPct: null,
};

/** Volumes are additive, so a client-side sum over the bin array is EXACT —
 *  no re-query, no interpolation, no rounding drift. */
export function totalsForBins(bins: StationBin[]): RangeTotals {
  if (bins.length === 0) return { ...EMPTY };
  let cut = 0;
  let fill = 0;
  let low = 0;
  let coverageWeighted = 0;
  let corridorCells = 0;
  let swathWeighted = 0;
  let swathLength = 0;
  for (const b of bins) {
    cut += b.cutVolumeM3;
    fill += b.fillVolumeM3;
    if (b.lowCoverage) low++;
    const w = coverageWeight(b);
    coverageWeighted += b.coveragePct * w;
    corridorCells += w;
    if (b.swathUsedPct != null) {
      const len = b.staEndInternalM - b.staStartInternalM;
      swathWeighted += b.swathUsedPct * len;
      swathLength += len;
    }
  }
  const from = bins[0].staStartInternalM;
  const to = bins[bins.length - 1].staEndInternalM;
  return {
    cut,
    fill,
    net: fill - cut,
    binCount: bins.length,
    lowCoverageCount: low,
    lengthM: to - from,
    fromInternalM: from,
    toInternalM: to,
    coveragePct: corridorCells > 0 ? coverageWeighted / corridorCells : null,
    swathUsedPct: swathLength > 0 ? swathWeighted / swathLength : null,
  };
}

export function totalsForRange(bins: StationBin[], fromBin: number, toBin: number): RangeTotals {
  if (bins.length === 0) return { ...EMPTY };
  const lo = Math.max(0, Math.min(fromBin, toBin));
  const hi = Math.min(bins.length - 1, Math.max(fromBin, toBin));
  return totalsForBins(bins.slice(lo, hi + 1));
}

/**
 * Which bin an internal station falls in, clamped to the run.
 *
 * Takes only the two fields the arithmetic needs, so the CLEARANCE run params
 * (`ClearanceRunParams`, same two field names, different everything else) reuse
 * this rather than re-inlining the floor-and-clamp in the clearance dock and
 * the 3D envelope.
 */
export function binIndexForStation(
  params: Pick<StationParams, 'stationFromInternalM' | 'stationIntervalM'>,
  nBins: number,
  internalStation: number,
): number {
  const i = Math.floor((internalStation - params.stationFromInternalM) / params.stationIntervalM);
  return Math.max(0, Math.min(nBins - 1, i));
}

/** The station a bin is quoted AT. Structurally typed for the same reason as
 *  `binIndexForStation` — a clearance bin has the same two station fields. */
export function binCentreStation(bin: Pick<StationBin, 'staStartInternalM' | 'staEndInternalM'>): number {
  return (bin.staStartInternalM + bin.staEndInternalM) / 2;
}

export interface SuperBin extends StationBin {
  /** Index range in the ORIGINAL bin array — the brush, table and CSV all
   *  stay at full resolution and only the chart aggregates. */
  fromBin: number;
  toBin: number;
}

/**
 * Collapse `bins` into at most `maxBars` groups by SUMMATION. Summation is
 * exact for volumes, so nothing is resampled or estimated; areas and coverage
 * become cell-weighted means, which is the only honest reduction for them.
 */
export function groupBins(bins: StationBin[], maxBars: number): { bins: SuperBin[]; groupSize: number } {
  const n = bins.length;
  const cap = Math.max(1, Math.floor(maxBars));
  if (n === 0) return { bins: [], groupSize: 1 };
  if (n <= cap) return { bins: bins.map((b, i) => ({ ...b, fromBin: i, toBin: i })), groupSize: 1 };

  const groupSize = Math.ceil(n / cap);
  const out: SuperBin[] = [];
  for (let start = 0, k = 0; start < n; start += groupSize, k++) {
    const part = bins.slice(start, Math.min(start + groupSize, n));
    const cells = part.reduce((s, b) => s + b.cellCount, 0);
    const binCells = part.reduce((s, b) => s + b.binCellCount, 0);
    const refCells = part.reduce((s, b) => s + coverageWeight(b), 0);
    const anyRef = part.some((b) => b.refCellCount != null);
    const swathUsed = totalsForBins(part).swathUsedPct;
    const sum = (f: (b: StationBin) => number) => part.reduce((s, b) => s + f(b), 0);
    const nullableSum = (f: (b: StationBin) => number | null) =>
      part.some((b) => f(b) == null) ? null : part.reduce((s, b) => s + (f(b) as number), 0);
    const weighted = (f: (b: StationBin) => number | null) => {
      let acc = 0;
      let w = 0;
      for (const b of part) {
        const v = f(b);
        if (v == null || b.binCellCount === 0) continue;
        acc += v * b.binCellCount;
        w += b.binCellCount;
      }
      return w > 0 ? acc / w : null;
    };
    const last = part[part.length - 1];
    const cut = sum((b) => b.cutVolumeM3);
    const fill = sum((b) => b.fillVolumeM3);
    out.push({
      i: k,
      staStartInternalM: part[0].staStartInternalM,
      staEndInternalM: last.staEndInternalM,
      staStartDisplayM: part[0].staStartDisplayM,
      staEndDisplayM: last.staEndDisplayM,
      cutVolumeM3: cut,
      fillVolumeM3: fill,
      netVolumeM3: fill - cut,
      cumulativeNetM3: last.cumulativeNetM3,
      cutAreaM2: weighted((b) => b.cutAreaM2) ?? 0,
      fillAreaM2: weighted((b) => b.fillAreaM2) ?? 0,
      cutVolumeAeaM3: nullableSum((b) => b.cutVolumeAeaM3),
      fillVolumeAeaM3: nullableSum((b) => b.fillVolumeAeaM3),
      cellCount: cells,
      binCellCount: binCells,
      ...(anyRef ? { refCellCount: refCells } : {}),
      coveragePct: refCells > 0 ? (100 * cells) / refCells : 0,
      ...(swathUsed != null ? { swathUsedPct: swathUsed } : {}),
      lowCoverage: part.some((b) => b.lowCoverage),
      minRadiusM: part.reduce<number | null>(
        (m, b) => (b.minRadiusM == null ? m : m == null ? b.minRadiusM : Math.min(m, b.minRadiusM)),
        null,
      ),
      minDzM: part.reduce<number | null>(
        (m, b) => (b.minDzM == null ? m : m == null ? b.minDzM : Math.min(m, b.minDzM)),
        null,
      ),
      maxDzM: part.reduce<number | null>(
        (m, b) => (b.maxDzM == null ? m : m == null ? b.maxDzM : Math.max(m, b.maxDzM)),
        null,
      ),
      meanDzM: weighted((b) => b.meanDzM),
      zTopMeanM: weighted((b) => b.zTopMeanM),
      fromBin: start,
      toBin: start + part.length - 1,
    });
  }
  return { bins: out, groupSize };
}

/**
 * Internal stations where the cumulative net curve crosses zero.
 *
 * Mirrors `scripts/station_bins.py` (T2.3 / T7.1): `cumulativeNetM3` on each
 * bin is the running sum AT THAT BIN'S END STATION (the value before bin 0
 * is 0 at bin 0's start). Near-zero float noise is snapped to exactly 0
 * using one array-wide scale (`|c| < 1e-9 * max(1, max|c|)`). An exact zero
 * at a bin's end station is one physical crossing point reported once, at
 * that end station — it is excluded from the generic pairwise sign-change
 * scan below so it is never double-counted by the pair it also belongs to.
 * A crossing between `cumulative[i-1]` and `cumulative[i]` (neither exactly
 * zero) lies inside bin `i` and is interpolated between bin `i`'s start and
 * end stations.
 */
export function balancePoints(bins: StationBin[]): number[] {
  const n = bins.length;
  if (n === 0) return [];
  const cumulative = bins.map((b) => b.cumulativeNetM3);
  const scale = Math.max(1, ...cumulative.map(Math.abs));
  const tol = 1e-9;
  const snapped = cumulative.map((c) => (Math.abs(c) < tol * scale ? 0 : c));

  const balance: number[] = [];
  for (let i = 0; i < n; i++) {
    if (snapped[i] === 0) balance.push(bins[i].staEndInternalM);
  }
  for (let i = 1; i < n; i++) {
    const a = snapped[i - 1];
    const b = snapped[i];
    if (a === 0 || b === 0) continue; // already emitted once above
    if (a < 0 !== b < 0) {
      const t = a / (a - b);
      balance.push(bins[i].staStartInternalM + t * (bins[i].staEndInternalM - bins[i].staStartInternalM));
    }
  }
  balance.sort((x, y) => x - y);
  return balance;
}

/** Net volume per metre — comparable even when the last bin is short. */
export function netPerMetre(bin: StationBin): number {
  const len = bin.staEndInternalM - bin.staStartInternalM;
  return len > 1e-9 ? bin.netVolumeM3 / len : 0;
}

/**
 * Symmetric ±p-th-percentile(|v|) domain, matching how surface_difference.py
 * picks its own colour range. The percentile is linearly interpolated
 * between the two bracketing sorted values (T7.2) rather than snapped to the
 * nearest sample index. Never collapses to zero width.
 */
export function symmetricDomain(values: number[], percentile = 98): [number, number] {
  const abs = values
    .map(Math.abs)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (abs.length === 0) return [-1, 1];
  const n = abs.length;
  const idx = (percentile / 100) * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const frac = idx - lo;
  const p = abs[lo] + (abs[hi] - abs[lo]) * frac;
  const a = p || 1;
  return [-a, a];
}

/** Fallback vertical padding when the offset planes are not loaded (m). */
const CLIP_HEIGHT_PAD_BINS = 5;
/** Vertical padding when they are (m) — the planes already bound the surfaces. */
const CLIP_HEIGHT_PAD_PLANES = 2;
/** Never hand the clip box a degenerate slab. */
const CLIP_HEIGHT_MIN = 2;

/**
 * The clip slab for "clip to station" (spec §11.1): one station bin long, the
 * swath wide, tall enough to contain the run, shifted onto the corridor's own
 * centre when the swath is asymmetric, and centred on the elevations the run
 * measured rather than on the alignment's z (ruling D35).
 *
 * Height comes from the offset planes when the Section tab has already
 * decoded them (they bound both surfaces exactly), otherwise from the bins'
 * `zTopMeanM` spread with a wider pad — a mean is not a bound, so the
 * fallback has to be generous or the slab clips the ground it is meant to
 * reveal. The vertical centre comes from the same two sources: the planes'
 * mid-elevation, else the mean of the bins' mean tops — a flat alignment's own
 * z says nothing about where the ground is.
 */
export function clipDimsForRun(
  params: Pick<StationParams, 'stationIntervalM' | 'swathLeftM' | 'swathRightM'>,
  bins: StationBin[],
  offsets: StationOffsets | null | undefined,
): ClipDims {
  let height = 0;
  let verticalCentre: number | undefined;
  if (offsets) {
    // `decodeStationOffsets` carves all three planes out of one buffer at
    // `nStations × nOffsets` each, so they are the same length by
    // construction; the `min` states that rather than assuming it.
    const n = Math.min(offsets.zTop.length, offsets.zBottom.length);
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < n; i++) {
      const b = offsets.zBottom[i];
      if (Number.isFinite(b)) lo = Math.min(lo, b);
      const t = offsets.zTop[i];
      if (Number.isFinite(t)) hi = Math.max(hi, t);
    }
    if (lo < Infinity && hi > -Infinity) {
      height = hi - lo + CLIP_HEIGHT_PAD_PLANES;
      verticalCentre = (lo + hi) / 2;
    }
  }
  if (!height) {
    let lo = Infinity;
    let hi = -Infinity;
    let sum = 0;
    let n = 0;
    for (const b of bins) {
      if (b.zTopMeanM == null || !Number.isFinite(b.zTopMeanM)) continue;
      lo = Math.min(lo, b.zTopMeanM);
      hi = Math.max(hi, b.zTopMeanM);
      sum += b.zTopMeanM;
      n++;
    }
    if (n > 0) {
      height = hi - lo + CLIP_HEIGHT_PAD_BINS;
      verticalCentre = sum / n;
    }
  }
  // Every dimension to 2 dp: these land in the walk toolbar's clip-box
  // settings, where `2.3500000000000005` is noise, and a centimetre is all
  // the slab needs.
  const r2 = (v: number) => Math.round(v * 100) / 100;
  return {
    length: r2(params.stationIntervalM),
    width: r2(params.swathLeftM + params.swathRightM),
    height: r2(Math.max(height, CLIP_HEIGHT_MIN)),
    // Positive = right. A 5 L / 15 R corridor's centre is 5 m right of the
    // centreline.
    lateralOffset: r2((params.swathRightM - params.swathLeftM) / 2),
    // Undefined when the run knows nothing about elevation — `clipBoxAtStation`
    // then falls back to the alignment's own z, which is all there is.
    verticalCentre: verticalCentre === undefined ? undefined : r2(verticalCentre),
  };
}
